/**
 * gridOverlay.js — сетка и краевые шкалы в стиле станционного вьювера GE.
 * Mikhmel Cornerstone4D DICOM Viewer Pro v5
 *
 * [v2.0 FIXES]:
 *  - Точка вместо запятой в цифрах (международный стандарт DICOM)
 *  - Сохранение ResizeObserver и слушателей для cleanup при destroy вьюпорта
 *  - Центральные оси чуть толще периферийных (SW_AXIS 0.9 vs SW_DASH 0.6)
 *  - Жёлтые шкалы: пропуск mm=0 (уже отрисовано центральной осью)
 *  - Экспорт destroyGridForViewport(vpId) для очистки
 *  - _cm: обработка отрицательных значений
 *  - Мобильная проверка через matchMedia с реактивным слушателем
 *  - Массив+join вместо конкатенации строк (для производительности)
 *
 * Три независимых слоя (свои тумблеры, все включены по умолчанию):
 *   grid   — пунктирная сетка через всё поле (шаг 30 мм) + центральные оси
 *            со штрихами каждые 10 мм и цифрами каждые 30 мм;
 *   scaleV — ЖЁЛТАЯ вертикальная шкала у правого края (риски 10 мм, крупные 50 мм);
 *   scaleH — ЖЁЛТАЯ горизонтальная шкала у нижнего края.
 * Масштаб честный в мм при любом зуме (пересчёт по камере).
 */
import { cornerstone } from './init.js';
import { getRenderingEngine, getViewportIds } from './viewportState.js';
import { log } from '../utils/logger.js';

// ══════════════════════════════════════════════════════════════
// STATE
// ══════════════════════════════════════════════════════════════

const _layers = { grid: true, scaleV: true, scaleH: true };
const _KEYS = { grid: 'ui.grid', scaleV: 'ui.scale.v', scaleH: 'ui.scale.h' };

// Хранилище cleanup-функций для каждого viewport
const _viewportCleanups = new Map();

// Реактивный media query для мобильных
let _isMobile = false;
const _mobileQuery = window.matchMedia ? window.matchMedia('(max-width: 768px)') : null;
if (_mobileQuery) {
  _isMobile = _mobileQuery.matches;
  _mobileQuery.addEventListener('change', (e) => {
    _isMobile = e.matches;
    if (!_isMobile) refreshGridOverlays(); // перерисовать при повороте в ландшафт
  });
}

// ══════════════════════════════════════════════════════════════
// PUBLIC API
// ══════════════════════════════════════════════════════════════

/**
 * Инициализация состояния слоёв из localStorage.
 * Выполняет одноразовую миграцию: все слои включены по умолчанию.
 */
export function initGridState() {
  // [6.0.4] Одноразовая миграция: слои принудительно включаются один раз.
  // Лечит «застрявшее» выключенное состояние из старого localStorage —
  // пользователь ожидает шкалы и сетку включёнными по умолчанию.
  try {
    if (localStorage.getItem('ui.gridMigr600') !== '1') {
      for (const k of Object.values(_KEYS)) localStorage.setItem(k, '1');
      localStorage.setItem('ui.gridMigr600', '1');
    }
  } catch (e) { /* приватный режим */ }

  for (const k of Object.keys(_layers)) {
    try {
      _layers[k] = localStorage.getItem(_KEYS[k]) !== '0';
    } catch (e) {
      _layers[k] = true;
    }
  }
}

/**
 * Проверить, включён ли слой.
 * @param {'grid'|'scaleV'|'scaleH'} name
 * @returns {boolean}
 */
export function isGridLayer(name) {
  return !!_layers[name];
}

/**
 * Включить/выключить слой.
 * @param {'grid'|'scaleV'|'scaleH'} name
 * @param {boolean} on
 */
export function setGridLayer(name, on) {
  if (!(name in _layers)) return;
  _layers[name] = !!on;
  try {
    localStorage.setItem(_KEYS[name], on ? '1' : '0');
  } catch (e) { /* приватный режим */ }
  refreshGridOverlays();
}

/**
 * Навесить/перерисовать слои на все 2D-окна текущей раскладки.
 */
export function refreshGridOverlays() {
  try {
    const re = getRenderingEngine();
    const vpIds = getViewportIds() || [];

    for (const vpId of vpIds) {
      const vp = re?.getViewport(vpId);
      const el = vp?.element;
      if (!el || String(vp?.type || '').toLowerCase().includes('3d')) continue;

      _draw(vpId);

      // Навешиваем слушатели если ещё не навешаны
      if (!_viewportCleanups.has(vpId)) {
        _bindViewportEvents(vpId, el);
      }
    }
  } catch (e) {
    log.warn('[Grid] refreshGridOverlays:', e.message);
  }
}

/**
 * Очистить сетку для конкретного viewport (при destroy).
 * @param {string} vpId
 */
export function destroyGridForViewport(vpId) {
  const cleanup = _viewportCleanups.get(vpId);
  if (cleanup) {
    cleanup();
    _viewportCleanups.delete(vpId);
  }

  // Также чистим SVG из DOM если элемент ещё жив
  try {
    const re = getRenderingEngine();
    const vp = re?.getViewport(vpId);
    const el = vp?.element;
    if (el) {
      const svg = el.querySelector(':scope > svg.vp-grid');
      if (svg) svg.remove();
    }
  } catch (e) {
    // viewport уже уничтожен — игнорируем
  }
}

/**
 * Очистить ВСЕ сетки (при полном destroy вьювера).
 */
export function destroyAllGrids() {
  for (const [vpId, cleanup] of _viewportCleanups) {
    try { cleanup(); } catch (e) { /* ignore */ }
  }
  _viewportCleanups.clear();
}

// ══════════════════════════════════════════════════════════════
// INTERNALS
// ══════════════════════════════════════════════════════════════

/**
 * Получить или создать SVG-элемент для viewport'а.
 * @param {HTMLElement} el
 * @returns {SVGSVGElement}
 */
function _svgFor(el) {
  let svg = el.querySelector(':scope > svg.vp-grid');
  if (!svg) {
    svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'vp-grid');
    svg.style.position = 'absolute';
    svg.style.top = '0';
    svg.style.left = '0';
    svg.style.width = '100%';
    svg.style.height = '100%';
    svg.style.pointerEvents = 'none'; // не перехватывать мышь
    svg.style.zIndex = '10';
    el.appendChild(svg);
  }
  return svg;
}

/**
 * Форматирование сантиметров: точка как десятичный разделитель (DICOM).
 * Отрицательные значения: знак минус сохраняется.
 * @param {number} mm
 * @returns {string}
 */
function _cm(mm) {
  if (!Number.isFinite(mm)) return '0.0';
  const val = (mm / 10).toFixed(1);
  // Для отрицательных: "-1.0" → "-1.0" (точка уже есть, запятую не ставим)
  return val;
}

/**
 * Навесить слушатели на viewport для автообновления сетки.
 * @param {string} vpId
 * @param {HTMLElement} el
 */
function _bindViewportEvents(vpId, el) {
  const onCamera = () => _draw(vpId);
  const onImage = () => _draw(vpId);
  const resizeObserver = new ResizeObserver(() => _draw(vpId));

  el.addEventListener(cornerstone.Enums.Events.CAMERA_MODIFIED, onCamera);
  el.addEventListener(cornerstone.Enums.Events.IMAGE_RENDERED, onImage);
  resizeObserver.observe(el);

  // Сохраняем cleanup-функцию
  _viewportCleanups.set(vpId, () => {
    el.removeEventListener(cornerstone.Enums.Events.CAMERA_MODIFIED, onCamera);
    el.removeEventListener(cornerstone.Enums.Events.IMAGE_RENDERED, onImage);
    resizeObserver.disconnect();
  });
}

/**
 * Основная отрисовка сетки и шкал для одного viewport'а.
 * @param {string} vpId
 */
function _draw(vpId) {
  try {
    const re = getRenderingEngine();
    const vp = re?.getViewport(vpId);
    const el = vp?.element;

    if (!vp || !el || String(vp.type || '').toLowerCase().includes('3d')) return;

    // Мобильная проверка (реактивная)
    if (_isMobile) {
      const svg = _svgFor(el);
      svg.innerHTML = '';
      return;
    }

    const svg = _svgFor(el);
    if (!_layers.grid && !_layers.scaleV && !_layers.scaleH) {
      svg.innerHTML = '';
      return;
    }

    const W = el.clientWidth;
    const H = el.clientHeight;
    if (!W || !H) return;

    svg.setAttribute('viewBox', `0 0 ${W} ${H}`);

    const ps = vp.getCamera?.()?.parallelScale;
    if (!Number.isFinite(ps) || ps <= 0) {
      svg.innerHTML = '';
      return;
    }

    const pxPerMm = H / (2 * ps);
    if (!Number.isFinite(pxPerMm) || pxPerMm <= 0) return;

    const cx = W / 2;
    const cy = H / 2;
    const s10 = 10 * pxPerMm;
    const s30 = 30 * pxPerMm;

    // Цвета и толщины (стиль GE: приглушённые, линии-волосинки)
    const WHITE = 'rgba(255,255,255,0.58)';
    const DIM = 'rgba(255,255,255,0.22)';
    const YELLOW = 'rgba(255,176,0,0.82)';
    const SW_DASH = 0.6;   // пунктирная сетка 30 мм
    const SW_AXIS = 0.9;   // центральные оси (чуть толще периферийных)
    const SW_TICK = 0.7;   // риски центральных осей (10/30 мм)
    const SW_SCALE = 1.0;  // жёлтые краевые шкалы

    const parts = [];

    // ════════════════════════════════════════════════════════════
    // СЕТКА: пунктир через всё поле (30 мм) + центральные оси
    // ════════════════════════════════════════════════════════════
    if (_layers.grid && s30 > 8) {
      // Пунктирная сетка
      const gridLines = [];
      for (let k = 1; ; k++) {
        const off = k * s30;
        if (off > Math.max(cx, W - cx) && off > Math.max(cy, H - cy)) break;

        for (const x of [cx - off, cx + off]) {
          if (x >= 0 && x <= W) gridLines.push(`M${x} 0L${x} ${H}`);
        }
        for (const y of [cy - off, cy + off]) {
          if (y >= 0 && y <= H) gridLines.push(`M0 ${y}L${W} ${y}`);
        }
      }
      if (gridLines.length > 0) {
        parts.push(`<path d="${gridLines.join('')}" stroke="${DIM}" stroke-width="${SW_DASH}" stroke-dasharray="1,4" fill="none"/>`);
      }

      // Центральные оси (сплошные, чуть толще)
      parts.push(`<line x1="${cx}" y1="0" x2="${cx}" y2="${H}" stroke="${DIM}" stroke-width="${SW_AXIS}"/>`);
      parts.push(`<line x1="0" y1="${cy}" x2="${W}" y2="${cy}" stroke="${DIM}" stroke-width="${SW_AXIS}"/>`);

      // Риски и цифры на центральных осях
      if (s10 > 3) {
        const tickMarks = [];
        const labels = [];

        for (let mm = 10; ; mm += 10) {
          const off = mm * pxPerMm;
          const inV = off <= Math.max(cy, H - cy);
          const inH = off <= Math.max(cx, W - cx);
          if (!inV && !inH) break;

          const half = (mm % 30 === 0) ? 8 : 5;
          const isLabel = mm % 30 === 0;
          const labelText = _cm(mm);

          // Вертикальные риски (на горизонтальной центральной оси)
          if (inV) {
            for (const y of [cy - off, cy + off]) {
              if (y < 0 || y > H) continue;
              tickMarks.push(`M${cx - half} ${y}L${cx + half} ${y}`);
              if (isLabel) {
                labels.push(`<text x="${cx + 11}" y="${y + 3}" fill="${WHITE}" font-size="9" font-family="monospace">${labelText}</text>`);
              }
            }
          }

          // Горизонтальные риски (на вертикальной центральной оси)
          if (inH) {
            for (const x of [cx - off, cx + off]) {
              if (x < 0 || x > W) continue;
              tickMarks.push(`M${x} ${cy - half}L${x} ${cy + half}`);
              if (isLabel) {
                labels.push(`<text x="${x}" y="${cy + 20}" fill="${WHITE}" font-size="9" font-family="monospace" text-anchor="middle">${labelText}</text>`);
              }
            }
          }
        }

        if (tickMarks.length > 0) {
          parts.push(`<path d="${tickMarks.join('')}" stroke="${WHITE}" stroke-width="${SW_TICK}" fill="none"/>`);
        }
        parts.push(...labels);
      }
    }

    // ════════════════════════════════════════════════════════════
    // ЖЁЛТЫЕ КРАЕВЫЕ ШКАЛЫ (стиль GE)
    // ════════════════════════════════════════════════════════════
    if (s10 > 3) {
      // Вертикальная шкала (правый край)
      if (_layers.scaleV) {
        const x0 = W - 1;
        const scaleLines = [`M${x0} 1L${x0} ${H - 1}`]; // базовая линия

        for (let mm = 10; ; mm += 10) {
          const off = mm * pxPerMm;
          if (off > Math.max(cy - 1, H - 1 - cy)) break;

          const len = (mm % 50 === 0) ? 9 : 5;
          for (const y of [cy - off, cy + off]) {
            if (y < 1 || y > H - 1) continue;
            scaleLines.push(`M${x0 - len} ${y}L${x0} ${y}`);
          }
        }

        parts.push(`<path d="${scaleLines.join('')}" stroke="${YELLOW}" stroke-width="${SW_SCALE}" fill="none"/>`);
      }

      // Горизонтальная шкала (нижний край)
      if (_layers.scaleH) {
        const y0 = H - 1;
        const scaleLines = [`M1 ${y0}L${W - 1} ${y0}`]; // базовая линия

        for (let mm = 10; ; mm += 10) {
          const off = mm * pxPerMm;
          if (off > Math.max(cx - 1, W - 1 - cx)) break;

          const len = (mm % 50 === 0) ? 9 : 5;
          for (const x of [cx - off, cx + off]) {
            if (x < 1 || x > W - 1) continue;
            scaleLines.push(`M${x} ${y0 - len}L${x} ${y0}`);
          }
        }

        parts.push(`<path d="${scaleLines.join('')}" stroke="${YELLOW}" stroke-width="${SW_SCALE}" fill="none"/>`);
      }
    }

    svg.innerHTML = parts.join('');
  } catch (e) {
    log.warn('[Grid] _draw:', e.message);
  }
}